import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { SearchStudentAction, TableColumnType } from '@core/enums/table';
import { AuthService } from '@core/services/auth.service';
import { StudentService } from '@core/services/student.service';
import { ToastService } from '@core/services/toast.service';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { SelectCountyComponent } from '@shared/components/dropdowns/select-county/select-county.component';
import { isNumber } from '@core/services/helper.service';
import { ReleaseStudentComponent } from './request-release-student/request-release-student.component';
import { StateService } from '@core/services/state.service';
import { take, tap } from 'rxjs';
import { IStateConfiguration } from '@core/interfaces/istate-configurations';
import { StudentDemographic } from '@core/enums/student-demographic-form';
import { States } from '@core/enums/states';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { ITableColumn } from '@core/interfaces/itable';
import { StudentStatus } from '@core/enums/student-request-reason';
import { DualEnrollChildComponent } from '../dual-enroll-child/dual-enroll-child.component';
import { SiteService } from '@core/services/site.service';
import { AdditionalFilters } from '@core/enums/additional-filters';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-add-search-child',
    templateUrl: './add-search-child.component.html',
    styleUrls: ['./add-search-child.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddSearchChildComponent implements OnInit {
  @ViewChild('countySelect') countySelect: SelectCountyComponent | undefined;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  payload: any = {};
  searchForm: FormGroup | any;
  userStateId?: number;

  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  pageSizeOptions: number[] = [10, 25, 50, 100];
  pageIndex: number = 0;
  pageSize: number = 10;
  totalData = 0;
  tableData?: any;
  ssidLabel = this.translocoService.translate('user.addNewChild.common.ssid');
  ssidPlaceholder = this.translocoService.translate('user.addNewChild.common.ssid')
  isNewHampshire = false;
  showCheckbox = false;
  disableAddChildBtn = true;

  get firstName() {
    return this.searchForm.get('firstName');
  }
  get lastName() {
    return this.searchForm.get('lastName');
  }
  get dob() {
    return this.searchForm.get('dob');
  }
  get SSID() {
    return this.searchForm.get('ssid');
  }
  get drdpId() {
    return this.searchForm.get('drdpId');
  }
  get countyOfProgramId() {
    return this.searchForm.get('countyOfProgramId');
  }
  get isDifferentChild() {
    return this.searchForm.get('isDifferentChild');
  }
  get additionalFilter() {
    return this.searchForm.get('additionalFilter');
  }
  get subFilter() {
    return AdditionalFilters;
  }

  tableColumns: ITableColumn[] = [
    {
      columnDef: 'firstName',
      header: this.translocoService.translate('user.addNewChild.common.firstName'),
      type: TableColumnType.text,
    },
    { columnDef: 'lastName', header: this.translocoService.translate('user.addNewChild.common.lastName'), type: TableColumnType.text },
    { columnDef: 'enrollmentType', header: this.translocoService.translate('user.addNewChild.search.enrollmentColumn'), type: TableColumnType.text },
    {
      columnDef: 'dob',
      header: this.translocoService.translate('user.addNewChild.common.dob'),
      dateFormat: 'mediumDate',
      type: TableColumnType.date,
    },
    { columnDef: 'ssid', header: this.translocoService.translate('user.addNewChild.common.ssid'), type: TableColumnType.text },
    { columnDef: 'drdpId', header: this.translocoService.translate('user.addNewChild.common.drdpId'), type: TableColumnType.text },
    {
      columnDef: 'previousCurrentSite',
      header: this.translocoService.translate('user.addNewChild.search.previousCurrentSiteColumn'),
      type: TableColumnType.text,
    },
  ];

  constructor(
    public modal: MatDialog,
    private fb: FormBuilder,
    public authService: AuthService,
    private studentService: StudentService,
    private router: Router,
    private toastService: ToastService,
    private stateService: StateService,
    private permission: PermissionService,
    private siteService: SiteService,
    private translocoService: TranslocoService
  ) {}

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    if(this.paginator)
      this.paginator._intl.itemsPerPageLabel = '';
  }

  ngOnInit(): void {
    if (this.permission.checkPermission(Permission.ViewCurrentEnrollment)) {
      this.tableColumns.push(
        {
          columnDef: 'previousCurrentAgency',
          header: this.translocoService.translate('user.addNewChild.search.previousCurrentAgencyColumn'),
          type: TableColumnType.text,
        }
      );
    }

    this.tableColumns.push(
      { columnDef: 'status', header: this.translocoService.translate('user.addNewChild.common.status'), type: TableColumnType.text },
      { columnDef: 'action', header: this.translocoService.translate('user.addNewChild.common.action'), type: TableColumnType.action }
    );

    const userSites = this.authService.getCurrentUser()?.sites;
    const canDualEnroll = userSites?.some((site: any) => site.site?.isDualEnrollment);

    if (this.permission.checkPermission(Permission.ManageDualEnrollment) && canDualEnroll) {
      this.tableColumns.push(
        {
          columnDef: 'hideIcon',
          type: TableColumnType.actionIcon,
          action: 'dualEnroll',
          header: this.translocoService.translate('user.addNewChild.search.dualEnrollmentColumn'),
          icon: 'users-alt',
          cursorClass: 'cursor-pointer',
          colorClass: 'text-drdpblue-300',
          info: this.translocoService.translate('user.addNewChild.search.dualEnrollmentInfo'),
          tooltip: this.translocoService.translate('user.addNewChild.search.dualEnrollmentTooltip')
        }
      );
    }

    this.userStateId = this.authService.getUserStateId();
    this.isNewHampshire = this.userStateId == States.newHampshire;
    this.getSsidLabel(this.userStateId);
    this.initializeForm();
  }

  initializeForm() {
    this.searchForm = this.fb.group({
      firstName: [null, [Validators.required, Validators.maxLength(100)]],
      lastName: [null, [Validators.required, Validators.maxLength(100)]],
      dob: [null, [Validators.required]],
      ssid: [null],
      drdpId: [null],
      countyOfProgramId: [null],
      isDifferentChild: [false],
      additionalFilter: AdditionalFilters.None
    });

    this.additionalFilter.valueChanges.subscribe((val: AdditionalFilters) => {
      if (val == AdditionalFilters.None) {
        this.searchForm.patchValue({
          ssid: null,
          drdpId: null,
          countyOfProgramId: null
        });
      }
    });
  }

  getSsidLabel(stateId: number): void {
    this.stateService.getStateConfigurations(stateId).pipe(
      take(1),
      tap((config: IStateConfiguration[]) => {
        const ssid = config.find(
          (x: any) => x.columnName === StudentDemographic.SSID
        );
        this.ssidLabel = ssid?.label ? ssid?.label : this.ssidLabel;
        this.ssidPlaceholder = ssid?.label ? ssid?.label : this.ssidLabel;

        const ssidColumnIndex = this.tableColumns.findIndex((col: any) => col.columnDef === 'ssid');
        if (this.isNewHampshire) {
          this.tableColumns[ssidColumnIndex].header = this.translocoService.translate('user.addNewChild.common.sasid');
        }
      })
      ).subscribe();
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return isNumber(charCode);
  }

  search() {
    if (this.searchForm.invalid) {
      this.toastService.error(this.translocoService.translate('user.addNewChild.common.requiredFieldsToast'));
      return;
    }

    this.payload = this.searchForm.value;

    this.payload.pageIndex = 0;
    this.payload.itemsPerPage = 10;
    this.paginateTable(false);
  }

  initializeTableData(res?: any): void {
    const modifiedItems = res?.items.map((item: any) => {
      let iconName = '';
      let tooltipText = '';
      let actionId = 0;
      let hideIcon = false;
      if (item?.isAddChild === true) {
        iconName = 'uil-user-plus';
        tooltipText = this.translocoService.translate('user.addNewChild.search.addChildTooltip');
        actionId = SearchStudentAction.AddChild;
        hideIcon = item?.isDualEnrollment;
      } else if (item?.isReleaseRequest === true) {
        iconName = 'uil-sign-out-alt';
        tooltipText = this.translocoService.translate('user.addNewChild.search.requestReleaseTooltip');
        actionId = SearchStudentAction.RequestRelease;
      } else {
        iconName = 'uil-file';
        tooltipText = this.translocoService.translate('user.addNewChild.search.viewStudentTooltip');
        actionId = SearchStudentAction.ViewStudent;
      }
      return {
        ...item,
        actionIcon: iconName,
        tooltipText: tooltipText,
        actionId,
        hideIcon
      };
    });
    this.dataSource = new MatTableDataSource(modifiedItems);
    this.totalData = res.totalData;
    this.tableData = modifiedItems;
  }

  clear() {
    this.searchForm.reset({ additionalFilter: AdditionalFilters.None });
    this.countySelect?.clearSelected();
    const empty: any = [];
    this.dataSource = new MatTableDataSource(empty);
    this.totalData = 0;
    this.tableData = [];
    this.pageSize = 10;
    this.paginator.pageSize = 10;
  }

  paginateTable(resetPage: boolean): void {
    this.studentService.addSearch(this.payload).subscribe((res: any) => {
      if (!res) {
        return;
      }

      if (res?.items.length < 1) {
        this.openNoResultsModal();
        return;
      }

      this.showCheckbox = true;
      if (resetPage) this.paginator.firstPage();
      this.initializeTableData(res);
      if (res.totalData > 25) this.pageSizeOptions.push(res.totalData);
    });
  }

  handleChosenCounty(event: any) {
    this.countyOfProgramId.setValue(event?.id);
  }

  handleAddChildBtn() {
    if (this.disableAddChildBtn) return;

    this.router.navigate(['/add-child'], {
      state: { searchValues: this.searchForm.value },
    });
  }

  handleCheckbox() {
    this.disableAddChildBtn = !this.disableAddChildBtn;
    this.isDifferentChild.setValue(!this.isDifferentChild.value);
  }

  openNoResultsModal(): void {
    var event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('user.addNewChild.search.noResultsTitle'),
      message: this.translocoService.translate('user.addNewChild.search.noResultsMessage'),
      primaryBtnClass: 'blue',
      primaryBtnText: this.translocoService.translate('user.addNewChild.common.addNewChild'),
      showCancel: true,
      cancelText: this.translocoService.translate('user.addNewChild.search.returnToSearch'),
    };
    const modalRef = this.modal.open(ConfirmationModalComponent, {
      data: event ? event : undefined,
    });
    modalRef.afterClosed().subscribe((res) => {
      if (res) {
        this.router.navigate(['/add-child'], {
          state: { searchValues: this.searchForm.value },
        });
      }
    });
  }

  openEnrollmentModal(event: any): void {
    if (event.action === 'dualEnroll') {
      if (event.data.isDualEnrolled) {
        this.toastService.error(this.translocoService.translate('user.addNewChild.search.alreadyDualEnrolledToast'));
        return;
      }
    }
    const modalRef = this.modal.open(DualEnrollChildComponent, {
      data: event ? event : undefined,
    });

    modalRef.afterClosed().subscribe((res) => {
      if (res) {
        this.search();
      }
    });
  }

  actionItem(event: any) {
    if (!event) {
      return;
    }
    if (event?.data.actionId == SearchStudentAction.AddChild) {
      this.router.navigateByUrl(`view-child/${event?.data?.id}`, {
        state: {
          isEdit: true,
          enrollDroppedChild: true,
          outOfState: event?.data?.outOfState
        },
      });
    } else if (event?.data.actionId == SearchStudentAction.RequestRelease) {
      this.openRequestReleaseModal(event);
    } else if (event?.data.actionId == SearchStudentAction.ViewStudent) {
      this.router.navigateByUrl(`view-child/${event?.data?.id}`);
    } else {
      this.toastService.error(this.translocoService.translate('user.addNewChild.search.noActionFoundToast'));
    }
  }

  openRequestReleaseModal(event: any) {
    if (event.data.id < 1) {
      this.toastService.error(this.translocoService.translate('user.addNewChild.search.noStudentDataToast'));
    } else if (!event.data.isDualEnrollment){
        const modalRef = this.modal.open(ReleaseStudentComponent, {
          data: event ? event : undefined,
        });
    } else {
      this.siteService.validateUserSite().pipe(take(1)).subscribe((res: boolean) => {
        if (res) {
          const modalRef = this.modal.open(ReleaseStudentComponent, {
            data: event ? event : undefined,
          });
        }
        else {
          this.toastService.error(this.translocoService.translate('user.addNewChild.search.dualEnrollmentSiteRequiredToast'));
        }
      });
    }
  }

  handleDate(event: any): void {
    this.dob.setValue(event);
  }
}
